import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Package, Truck, Globe2, Zap, Shield, TrendingUp, 
  Clock, CheckCircle, ArrowRight, Warehouse
} from 'lucide-react';
import { QUOTE_URL } from '../lib/links';

const FulfillmentSection = () => {
  const { t, language } = useLanguage();

  const marketplaces = [
    { name: 'Amazon', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg' },
    { name: 'Amazon FBA', logo: 'https://www.google.com/s2/favicons?domain=sellercentral.amazon.com&sz=128' },
    { name: 'Amazon FBM', logo: '/logos/amazon-fbm.png' },
    { name: 'eBay', logo: 'https://upload.wikimedia.org/wikipedia/commons/1/1b/EBay_logo.svg' },
    { name: 'Mercado Libre', logo: '/logos/mercadolibre.svg' },
    { name: 'Shopify', logo: 'https://upload.wikimedia.org/wikipedia/commons/0/0e/Shopify_logo_2018.svg' },
    { name: 'SHEIN', logo: 'https://www.google.com/s2/favicons?domain=shein.com&sz=128' },
    { name: 'Walmart', logo: 'https://www.google.com/s2/favicons?domain=walmart.com&sz=128' },
    { name: 'Tiendanube', logo: 'https://www.google.com/s2/favicons?domain=tiendanube.com&sz=128' },
    { name: 'Coppel', logo: 'https://www.google.com/s2/favicons?domain=coppel.com&sz=128' },
    { name: 'Liverpool', logo: 'https://www.google.com/s2/favicons?domain=liverpool.com.mx&sz=128' },
    { name: 'Magento', logo: 'https://www.google.com/s2/favicons?domain=business.adobe.com&sz=128' },
    { name: 'VTEX', logo: 'https://www.google.com/s2/favicons?domain=vtex.com&sz=128' },
    { name: 'WooCommerce', logo: 'https://www.google.com/s2/favicons?domain=woocommerce.com&sz=128' },
    { name: 'Wix', logo: 'https://www.google.com/s2/favicons?domain=wix.com&sz=128' },
    { name: 'Ripley', logo: 'https://www.google.com/s2/favicons?domain=ripley.cl&sz=128' },
    { name: 'Shift4Shop', logo: 'https://www.google.com/s2/favicons?domain=shift4shop.com&sz=128' },
    { name: 'Ventiapp', logo: 'https://www.google.com/s2/favicons?domain=ventia.com&sz=128' },
  ];

  // Split into two rows for marquee (opposite directions)
  const marqueeRow1 = [...marketplaces.slice(0, 9), ...marketplaces.slice(0, 9)];
  const marqueeRow2 = [...marketplaces.slice(9), ...marketplaces.slice(9)];

  const benefits = [
    {
      icon: Clock,
      title: { en: 'Same-Day Shipping', es: 'Envío en el Día', pt: 'Envio no Mesmo Dia', zh: '当日发货' },
      desc: { en: 'Orders before 2PM ship same day', es: 'Pedidos antes de las 14hs salen el mismo día', pt: 'Pedidos antes das 14h saem no mesmo dia', zh: '下午 2 点前的订单当日发出' }
    },
    {
      icon: Globe2,
      title: { en: 'Global Reach', es: 'Alcance Global', pt: 'Alcance Global', zh: '全球覆盖' },
      desc: { en: 'Ship to LATAM, Africa & Middle East', es: 'Envíos a LATAM, África y Medio Oriente', pt: 'Envios para LATAM, África e Oriente Médio', zh: '发货至拉美、非洲和中东' }
    },
    {
      icon: Shield,
      title: { en: 'Inventory Protection', es: 'Protección de Inventario', pt: 'Proteção de Inventário', zh: '库存保护' },
      desc: { en: 'Climate-controlled & insured storage', es: 'Almacén climatizado y asegurado', pt: 'Armazém climatizado e segurado', zh: '恒温保险存储' }
    },
    {
      icon: Zap,
      title: { en: 'API Integration', es: 'Integración API', pt: 'Integração API', zh: 'API 集成' },
      desc: { en: 'Real-time sync with all platforms', es: 'Sincronización en tiempo real', pt: 'Sincronização em tempo real', zh: '与所有平台实时同步' }
    },
    {
      icon: TrendingUp,
      title: { en: 'Scale Easily', es: 'Escala Fácil', pt: 'Escale Facilmente', zh: '轻松扩展' },
      desc: { en: 'From 10 to 10,000+ orders/day', es: 'De 10 a 10.000+ pedidos/día', pt: 'De 10 a 10.000+ pedidos/dia', zh: '从每天 10 单到 10,000+ 单' }
    },
    {
      icon: Package,
      title: { en: 'Custom Packaging', es: 'Packaging Personalizado', pt: 'Embalagem Personalizada', zh: '定制包装' },
      desc: { en: 'Your brand, your experience', es: 'Tu marca, tu experiencia', pt: 'Sua marca, sua experiência', zh: '您的品牌,您的体验' }
    },
  ];

  // Marketplace card — used in marquee
  const MarketplaceCard = ({ mp }) => (
    <div
      className="group flex-shrink-0 flex items-center gap-3 bg-white rounded-xl px-5 py-3 min-w-[180px] hover:scale-105 hover:shadow-[0_10px_30px_rgba(122,193,66,0.35)] transition-all duration-300 cursor-default"
      title={mp.name}
    >
      <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
        <img
          src={mp.logo}
          alt={mp.name}
          className="max-w-full max-h-full object-contain"
          loading="lazy"
          onError={(e) => { e.target.style.visibility = 'hidden'; }}
        />
      </div>
      <div className="flex flex-col">
        <span className="text-gray-900 font-bold text-sm leading-tight">{mp.name}</span>
        <span className="text-emerald-600 font-mono text-[9px] uppercase tracking-wider flex items-center gap-1">
          <span className="w-1 h-1 bg-emerald-500 rounded-full" />
          {t('fulfillment.integrated')}
        </span>
      </div>
    </div>
  );

  return (
    <section id="fulfillment" className="relative py-24 overflow-hidden">
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#0f1a0a] to-[#0a0a0a]" />
      
      {/* Animated grid pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(#7AC142 1px, transparent 1px), linear-gradient(90deg, #7AC142 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="eyebrow mb-4">
              <Warehouse className="w-4 h-4" />
              {t('fulfillment.badge')}
            </span>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6">
              {t('fulfillment.title').split('Miami')[0]}
              <span className="text-[#7AC142]">Miami</span>
              {t('fulfillment.title').split('Miami')[1] || ''}
            </h2>
            
            <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              {t('fulfillment.subtitle')}
            </p>
          </motion.div>
        </div>

        {/* Main Content: Miami hub strip + benefits */}
        <div className="space-y-6 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6 md:gap-0 md:divide-x divide-white/10 bg-black/50 border border-white/10 rounded-2xl px-8 py-6 backdrop-blur-sm"
            data-testid="fulfillment-hub"
          >
            <div className="flex items-center justify-center">
              <div className="px-4 py-2 bg-[#7AC142] text-black font-bold text-sm rounded-full flex items-center gap-2">
                <div className="w-2 h-2 bg-black rounded-full animate-pulse" />
                MIAMI, FL - USA
              </div>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-[#7AC142] font-mono">24/7</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">{t('fulfillment.operations')}</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-[#7AC142] font-mono">DG</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">{t('hero.stats.certified')}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group p-5 bg-white/5 border border-white/10 hover:border-[#7AC142]/30 hover:bg-[#7AC142]/5 transition-all rounded-lg"
                >
                  <benefit.icon className="w-8 h-8 text-[#7AC142] mb-3 group-hover:scale-110 transition-transform" />
                  <h4 className="font-bold text-white mb-1">{benefit.title[t('lang') || 'en']}</h4>
                  <p className="text-sm text-gray-400">{benefit.desc[t('lang') || 'en']}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* === MARKETPLACE SHOWCASE === */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          {/* Headline */}
          <div className="text-center mb-10">
            <span className="eyebrow mb-4">
              <Zap className="w-4 h-4" />
              {t('fulfillment.marketplaces.badge')}
            </span>
            <h3 className="text-3xl md:text-4xl font-black text-white tracking-tight">
              {t('fulfillment.marketplaces.title')}{' '}
              <span className="text-[#7AC142]">{t('fulfillment.marketplaces.titleAccent')}</span>
            </h3>
            <p className="mt-3 text-gray-400 text-base max-w-2xl mx-auto">
              {t('fulfillment.marketplaces.subtitle')}
            </p>
          </div>

          {/* Marquee container with edge fades */}
          <div className="marquee-container relative overflow-hidden py-2 space-y-4"
            style={{
              maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
              WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)'
            }}
          >
            {/* Row 1 — left */}
            <div className="marquee-track gap-3">
              {marqueeRow1.map((mp, i) => (
                <MarketplaceCard key={`r1-${i}`} mp={mp} />
              ))}
            </div>
            {/* Row 2 — right */}
            <div className="marquee-track-reverse gap-3">
              {marqueeRow2.map((mp, i) => (
                <MarketplaceCard key={`r2-${i}`} mp={mp} />
              ))}
            </div>
          </div>

          {/* Bottom callout */}
          <div className="mt-8 max-w-3xl mx-auto bg-gradient-to-r from-[#7AC142]/5 via-[#7AC142]/10 to-[#7AC142]/5 border border-[#7AC142]/20 rounded-xl p-5 text-center">
            <p className="text-white font-bold text-base">
              {t('fulfillment.moreMarketplaces')}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {t('fulfillment.customIntegration')}
            </p>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="bg-gradient-to-r from-[#7AC142]/20 via-[#7AC142]/10 to-[#7AC142]/20 border border-[#7AC142]/30 rounded-2xl p-8 md:p-12 text-center backdrop-blur-sm">
            {/* Decorative elements */}
            <div className="absolute top-4 left-4 w-20 h-20 border border-[#7AC142]/20 rounded-full" />
            <div className="absolute bottom-4 right-4 w-32 h-32 border border-[#7AC142]/10 rounded-full" />
            
            <div className="relative">
              <div className="flex items-center justify-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-[#7AC142]" />
                <span className="text-[#7AC142] font-mono text-sm uppercase tracking-wider">
                  {t('fulfillment.ctaBadge')}
                </span>
              </div>
              
              <h3 className="text-2xl md:text-4xl font-black text-white mb-4">
                {t('fulfillment.ctaTitle')}
              </h3>
              
              <p className="text-gray-400 max-w-2xl mx-auto mb-8">
                {t('fulfillment.ctaDesc')}
              </p>
              
              <a
                href={QUOTE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 bg-[#7AC142] text-black px-8 py-4 text-lg font-bold uppercase tracking-wider hover:bg-[#8BD152] transition-all rounded-lg shadow-lg shadow-[#7AC142]/25 hover:shadow-[#7AC142]/40"
                data-testid="fulfillment-cta-btn"
              >
                {t('fulfillment.ctaButton')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              
              <p className="mt-6 text-sm text-gray-500">
                {t('fulfillment.ctaSubtext')}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FulfillmentSection;
